import React, { useState, useEffect, useRef } from 'react';
import { X, Loader2, Search } from 'lucide-react';
import useApi from '../../hooks/useApi';

// --- Hook useDebounce (Interno) ---
const useDebounce = (value, delay) => {
    const [debouncedValue, setDebouncedValue] = useState(value);
    useEffect(() => {
        const handler = setTimeout(() => {
            setDebouncedValue(value);
        }, delay);
        return () => {
            clearTimeout(handler);
        };
    }, [value, delay]);
    return debouncedValue;
};

// --- COMPONENTE DE BUSCA DE CLIENTE ---
const ClienteSearchInput = ({ onClienteSelect, initialCliente = null }) => {
    const { apiFetch } = useApi();
    const [searchTerm, setSearchTerm] = useState(initialCliente ? initialCliente.name : '');
    const [results, setResults] = useState([]);
    const [loading, setLoading] = useState(false);
    const [selected, setSelected] = useState(initialCliente);
    const debouncedSearchTerm = useDebounce(searchTerm, 300);
    const wrapperRef = useRef(null);

    // Sincroniza se o cliente inicial mudar
    useEffect(() => {
        if (initialCliente) {
            setSearchTerm(initialCliente.name);
            setSelected(initialCliente);
        }
    }, [initialCliente]);

    useEffect(() => {
        if (debouncedSearchTerm && (!selected || debouncedSearchTerm !== selected.name)) {
            setLoading(true);
            apiFetch(`/contas-a-receber/clientes?search=${debouncedSearchTerm}`)
                .then(data => setResults(data || []))
                .catch(err => console.error(err))
                .finally(() => setLoading(false));
        } else {
            setResults([]);
        }
    }, [debouncedSearchTerm, apiFetch, selected]);

    // Fecha dropdown ao clicar fora
    useEffect(() => {
        function handleClickOutside(event) {
            if (wrapperRef.current && !wrapperRef.current.contains(event.target)) {
                setResults([]);
            }
        }
        document.addEventListener("mousedown", handleClickOutside);
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, [wrapperRef]);

    const handleSelect = (cliente) => {
        setSearchTerm(cliente.name);
        setSelected(cliente);
        onClienteSelect(cliente);
        setResults([]);
    };

    const handleInputChange = (e) => {
        setSearchTerm(e.target.value);
        setSelected(null);
        onClienteSelect(null);
    };

    return (
        <div className="relative" ref={wrapperRef}>
            <input
                type="text"
                value={searchTerm}
                onChange={handleInputChange}
                placeholder="Pesquisar cliente..."
                className="w-full p-2 mt-1 border rounded-md"
                required
            />
            {loading && <Loader2 size={16} className="animate-spin absolute right-3 top-3.5 text-gray-400" />}
            {results.length > 0 && (
                <ul className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-md shadow-lg max-h-60 overflow-y-auto">
                    {results.map(c => (
                        <li 
                            key={c.id} 
                            onClick={() => handleSelect(c)}
                            className="p-3 hover:bg-gray-100 cursor-pointer border-b last:border-0"
                        >
                            <p className="font-medium">{c.name}</p>
                            <p className="text-sm text-gray-500">{c.document_id || `Cód: ${c.client_number}`}</p>
                        </li>
                    ))}
                </ul>
            )}
        </div>
    );
};

// --- MODAL PRINCIPAL ---
const NovoRecebimentoModal = ({ onClose, onSave, formatCurrency, initialData = null, faturaId = null }) => {
    const { apiFetch } = useApi();
    const [loading, setLoading] = useState(false);
    const [dataLoading, setDataLoading] = useState(!!faturaId); 
    const [error, setError] = useState('');
    
    // --- ESTADOS ---
    const [selectedCliente, setSelectedCliente] = useState(
        initialData ? { id: initialData.cliente_id, name: initialData.cliente_nome } : null
    );
    const [numeroNF, setNumeroNF] = useState('');
    const [osNsa, setOsNsa] = useState(initialData?.os_number || '');
    const [dataEmissao, setDataEmissao] = useState(new Date().toISOString().split('T')[0]);
    const [dataEnvio, setDataEnvio] = useState(''); 

    const [valorTotalFatura, setValorTotalFatura] = useState(initialData?.valor_total || 0);
    const [observacoes, setObservacoes] = useState('');

    const [numeroParcelas, setNumeroParcelas] = useState(1);
    const [meioPagamento, setMeioPagamento] = useState('pix'); 
    const [parcelas, setParcelas] = useState([]);

    // --- EFEITO PARA CARREGAR DADOS INICIAIS DA AGENDA/OS ---
    useEffect(() => {
        if (initialData && !faturaId) {
            if (initialData.client_id) {
                setSelectedCliente({ 
                    id: initialData.client_id, 
                    name: initialData.client_name || '' 
                });
            }
            if (initialData.os_number) setOsNsa(initialData.os_number);
            else if (initialData.description && initialData.description.includes('#')) {
                const match = initialData.description.match(/#(\d+)/);
                if (match) setOsNsa(match[1]);
            }

            if (initialData.amount) setValorTotalFatura(parseFloat(initialData.amount));
            if (initialData.description) setObservacoes(initialData.description);
        }
    }, [initialData, faturaId]);

    // --- LÓGICA DE EDIÇÃO (Carregar Dados) ---
    useEffect(() => {
        if (faturaId) {
            const loadFatura = async () => {
                setDataLoading(true);
                try {
                    const data = await apiFetch(`/contas-a-receber/fatura/${faturaId}`);
                    
                    setSelectedCliente({ id: data.cliente_id, name: data.cliente_nome });
                    setNumeroNF(data.nf_numero || '');
                    setOsNsa(data.os_nsa || '');
                    setDataEmissao(data.data_emissao.split('T')[0]);
                    
                    if (data.data_envio) {
                        setDataEnvio(data.data_envio.split('T')[0]);
                    }

                    setValorTotalFatura(data.valor_total);
                    setObservacoes(data.observacoes || '');
                    
                    if (data.parcelas && data.parcelas.length > 0) {
                        setNumeroParcelas(data.parcelas.length);
                        setMeioPagamento(data.parcelas[0].meio_pagamento || 'pix');
                        
                        const mappedParcelas = data.parcelas.map((p, i) => ({
                            id: i, 
                            real_id: p.id, 
                            numero_parcela: p.numero_parcela,
                            data_vencimento: p.data_vencimento.split('T')[0],
                            valor_parcela: p.valor_parcela,
                            pix_chave: p.pix_chave || '',
                            boleto_numero: p.boleto_numero || '',
                            boleto_cod_barras: p.boleto_cod_barras || '',
                            status: p.status
                        }));
                        setParcelas(mappedParcelas);
                    }
                } catch (err) {
                    setError("Erro ao carregar fatura: " + err.message);
                } finally {
                    setDataLoading(false);
                }
            };
            loadFatura();
        }
    }, [faturaId, apiFetch]);

    // --- CÁLCULO AUTOMÁTICO DE PARCELAS ---
    useEffect(() => {
        if (dataLoading) return;
        if (faturaId && parcelas.length === numeroParcelas) return;

        const dataBase = dataEmissao || new Date().toISOString().split('T')[0];
        const valorBase = valorTotalFatura;
        const valorPorParcela = numeroParcelas > 0 ? (valorBase / numeroParcelas).toFixed(2) : 0;

        setParcelas(Array.from({ length: numeroParcelas }, (_, i) => {
            const vencimento = new Date(dataBase);
            vencimento.setMonth(vencimento.getMonth() + (i + 1));
            
            const existing = parcelas[i] || {};

            return {
                id: i,
                numero_parcela: i + 1,
                data_vencimento: existing.data_vencimento || vencimento.toISOString().split('T')[0],
                valor_parcela: valorPorParcela,
                pix_chave: meioPagamento === 'pix' ? (existing.pix_chave || '') : null,
                boleto_numero: meioPagamento === 'boleto' ? (existing.boleto_numero || '') : null,
                boleto_cod_barras: meioPagamento === 'boleto' ? (existing.boleto_cod_barras || '') : null,
            };
        }));
    }, [numeroParcelas, meioPagamento, dataEmissao, initialData, faturaId, valorTotalFatura]);

    const handleParcelaChange = (index, field, value) => {
        const novasParcelas = [...parcelas];
        novasParcelas[index] = { ...novasParcelas[index], [field]: value };
        setParcelas(novasParcelas);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError('');
        setLoading(true);

        if (!selectedCliente) {
            setError('Por favor, pesquise e selecione um cliente.');
            setLoading(false);
            return;
        }

        const headerData = {
            cliente_id: parseInt(selectedCliente.id, 10),
            nf_numero: numeroNF || null,
            os_nsa: osNsa || null,
            data_emissao: dataEmissao,
            data_envio: dataEnvio || null,
            valor_total: valorTotalFatura,
            observacoes: observacoes || null,
            os_nsa_id: initialData?.os_nsa_id || null // Vincula ID da OS se existir
        };

        try {
            if (faturaId) {
                await apiFetch(`/contas-a-receber/fatura/${faturaId}`, { 
                    method: 'PUT', 
                    body: headerData 
                });

                const promises = parcelas.map(p => {
                    if (p.real_id) {
                         return apiFetch(`/contas-a-receber/parcela/${p.real_id}`, {
                            method: 'PUT',
                            body: {
                                data_vencimento: p.data_vencimento,
                                valor_parcela: p.valor_parcela,
                                pix_chave: p.pix_chave || null,
                                boleto_numero: p.boleto_numero || null,
                                boleto_cod_barras: p.boleto_cod_barras || null,
                            }
                        });
                    }
                    return null;
                }).filter(Boolean);
                
                await Promise.all(promises);
            } else {
                const createPayload = {
                    fatura: headerData,
                    parcelas: parcelas.map(p => ({
                        numero_parcela: p.numero_parcela,
                        data_vencimento: p.data_vencimento,
                        valor_parcela: parseFloat(p.valor_parcela) || 0,
                        meio_pagamento: meioPagamento,
                        pix_chave: p.pix_chave || null,
                        boleto_numero: p.boleto_numero || null,
                        boleto_cod_barras: p.boleto_cod_barras || null,
                    }))
                };
                await apiFetch('/contas-a-receber', { method: 'POST', body: createPayload });
            }

            onSave(); 
            onClose(); 
        } catch (err) {
            console.error("Erro ao salvar:", err);
            setError(err.message);
        } finally {
            setLoading(false);
        }
    };

    if (dataLoading) {
        return (
            <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black bg-opacity-50">
                <div className="bg-white rounded-lg p-6 shadow-xl">
                    <Loader2 className="animate-spin h-8 w-8 text-blue-600" />
                </div>
            </div>
        );
    }

    return (
        <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black bg-opacity-50 p-4">
            <div className="bg-white rounded-lg shadow-xl w-full max-w-3xl max-h-[90vh] flex flex-col" onClick={e => e.stopPropagation()}>
                <div className="flex items-center justify-between p-4 border-b">
                    <h2 className="text-xl font-bold text-gray-800">
                        {faturaId ? `Editar Recebimento (NF: ${numeroNF || 'S/N'})` : 'Lançar Novo Recebimento'}
                    </h2>
                    <button onClick={onClose} className="p-2 rounded-full hover:bg-gray-200"><X size={20} /></button>
                </div>
                
                <form onSubmit={handleSubmit} className="flex-1 overflow-y-auto">
                    <div className="p-6 space-y-6">
                        {error && <p className="p-3 text-sm text-red-700 bg-red-100 rounded-md">{error}</p>}
                        
                        {initialData && !faturaId && (
                             <div className="p-3 bg-blue-50 text-blue-800 border border-blue-200 rounded-md text-sm mb-4 flex items-center gap-2">
                                <Search size={16} />
                                <strong>Importando dados da OS:</strong> Campos preenchidos automaticamente.
                            </div>
                        )}

                        <div className="p-4 border rounded-lg">
                            <h3 className="mb-4 text-lg font-semibold">Dados da Fatura</h3>
                            
                            {/* LAYOUT RESTAURADO: GRID DE 4 COLUNAS SIMPLES */}
                            <div className="grid grid-cols-1 gap-4 md:grid-cols-4">
                                <div>
                                    <label className="block text-sm font-medium text-gray-700">Cliente*</label>
                                    <ClienteSearchInput 
                                        onClienteSelect={setSelectedCliente}
                                        initialCliente={selectedCliente}
                                    />
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700">Nº da NF</label>
                                    <input type="text" value={numeroNF} onChange={e => setNumeroNF(e.target.value)} className="w-full p-2 mt-1 border rounded-md" />
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700">OS/NSA</label>
                                    <input type="text" value={osNsa} onChange={e => setOsNsa(e.target.value)} className="w-full p-2 mt-1 border rounded-md" />
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700">Data de Emissão*</label>
                                    <input type="date" value={dataEmissao} onChange={e => setDataEmissao(e.target.value)} className="w-full p-2 mt-1 border rounded-md" required />
                                </div>
                                
                                {/* LINHA 2 */}
                                <div>
                                    <label className="block text-sm font-medium text-gray-700">Data Envio Cliente</label>
                                    <input type="date" value={dataEnvio} onChange={e => setDataEnvio(e.target.value)} className="w-full p-2 mt-1 border rounded-md" />
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700">Nº de Parcelas*</label>
                                    <select value={numeroParcelas} onChange={e => setNumeroParcelas(Number(e.target.value))} className="w-full p-2 mt-1 border rounded-md" disabled={!!faturaId}>
                                        {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12].map(n => <option key={n} value={n}>{n}</option>)}
                                    </select>
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700">Meio de Pagamento*</label>
                                    <select value={meioPagamento} onChange={e => setMeioPagamento(e.target.value)} className="w-full p-2 mt-1 border rounded-md">
                                        <option value="pix">PIX</option>
                                        <option value="boleto">Boleto</option>
                                        <option value="dinheiro">Dinheiro</option>
                                    </select>
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700">Valor Total da Fatura</label>
                                    <input 
                                        type="number" step="0.01" 
                                        value={valorTotalFatura} 
                                        onChange={e => setValorTotalFatura(parseFloat(e.target.value)||0)} 
                                        className="w-full p-2 mt-1 border rounded-md font-bold" 
                                    />
                                </div>

                                <div className="md:col-span-4">
                                    <label className="block text-sm font-medium text-gray-700">Observações</label>
                                    <textarea 
                                        value={observacoes} 
                                        onChange={e => setObservacoes(e.target.value)} 
                                        className="w-full p-2 mt-1 border rounded-md"
                                        rows="2"
                                        placeholder="Detalhes adicionais sobre a fatura..."
                                    ></textarea>
                                </div>
                            </div>
                        </div>

                        <div className="p-4 border rounded-lg">
                            <h3 className="mb-4 text-lg font-semibold">Parcelas</h3>
                            <div className="space-y-4">
                                {parcelas.map((parcela, index) => {
                                    const isPaga = parcela.status === 'paga';
                                    return (
                                        <div key={parcela.id} className={`p-3 rounded-md ${isPaga ? 'bg-green-50 border border-green-200' : 'bg-gray-50'}`}>
                                            <div className="grid grid-cols-1 gap-4 md:grid-cols-4">
                                                <div>
                                                    <label className="text-xs font-medium text-gray-600">Parcela</label>
                                                    <input type="text" value={parcela.numero_parcela} disabled className="w-full p-2 mt-1 bg-gray-200 border rounded-md" />
                                                </div>
                                                <div>
                                                    <label className="text-xs font-medium text-gray-600">Vencimento*</label>
                                                    <input type="date" value={parcela.data_vencimento} onChange={e => handleParcelaChange(index, 'data_vencimento', e.target.value)} className="w-full p-2 mt-1 border rounded-md" required disabled={isPaga} />
                                                </div>
                                                <div>
                                                    <label className="text-xs font-medium text-gray-600">Valor*</label>
                                                    <input type="number" step="0.01" value={parcela.valor_parcela} onChange={e => handleParcelaChange(index, 'valor_parcela', e.target.value)} className="w-full p-2 mt-1 border rounded-md" required disabled={isPaga} />
                                                </div>
                                                
                                                {meioPagamento === 'pix' && (
                                                    <div>
                                                        <label className="text-xs font-medium text-gray-600">Chave PIX</label>
                                                        <input type="text" value={parcela.pix_chave || ''} onChange={e => handleParcelaChange(index, 'pix_chave', e.target.value)} className="w-full p-2 mt-1 border rounded-md" disabled={isPaga} />
                                                    </div>
                                                )}
                                                {meioPagamento === 'boleto' && (
                                                    <>
                                                        <div>
                                                            <label className="text-xs font-medium text-gray-600">Nº do Boleto</label>
                                                            <input type="text" value={parcela.boleto_numero || ''} onChange={e => handleParcelaChange(index, 'boleto_numero', e.target.value)} className="w-full p-2 mt-1 border rounded-md" disabled={isPaga} />
                                                        </div>
                                                        <div className="md:col-span-2">
                                                            <label className="text-xs font-medium text-gray-600">Cód. de Barras</label>
                                                            <input type="text" value={parcela.boleto_cod_barras || ''} onChange={e => handleParcelaChange(index, 'boleto_cod_barras', e.target.value)} className="w-full p-2 mt-1 border rounded-md" disabled={isPaga} />
                                                        </div>
                                                    </>
                                                )}
                                                
                                                {isPaga && (
                                                    <div className="md:col-span-4 text-xs text-green-600 font-bold text-center mt-1">
                                                        Parcela Paga
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        </div>
                    </div>
                    <div className="flex justify-end gap-2 p-4 mt-4 bg-gray-50 border-t">
                        <button type="button" onClick={onClose} className="px-4 py-2 font-semibold bg-gray-200 rounded-md">Cancelar</button>
                        <button type="submit" disabled={loading} className="flex items-center justify-center px-4 py-2 font-semibold text-white bg-blue-600 rounded-md disabled:bg-blue-300">
                            {loading ? <Loader2 className="animate-spin" /> : (faturaId ? 'Salvar Alterações' : 'Salvar Fatura')}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
};

export default NovoRecebimentoModal;